import { PlusOutlined } from '@ant-design/icons';
import { Button, RadioInput } from '@mm/components';
import { useWhitelabelConfig } from '@mm/hooks/src/use-whitelabel-config';
import { GoHighLevelIcon } from '@mm/icons';
import { useConnectionCRUDActions } from '@mm/state/actions/ConnectionsCRUDActions';
import { useTagsCRUDActions } from '@mm/state/actions/TagsCRUDActions';
import ConnectionModel, {
  GoHighLevelOauthToken,
} from '@mm/state/model/ConnectionModel';
import { selectAudienceSorted } from '@mm/state/selectors/messengerAdSelectors';
import { tagsOfKinds } from '@mm/state/selectors/tags';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { ReactComponent as CustomersAiLogo } from 'assets/icons/customersai-logo.svg';
import classNames from 'classnames';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { GoHighLevelConnectionEditor } from './GoHighLevelConnectionEditor';
import styles from './GoHighLevelIntegrations.module.css';
import { useConnectGoHighLevel } from './use-connect-gohighlevel';

const validConnection = (
  mode: 'all' | 'audience',
  connection: ConnectionModel,
) =>
  (mode === 'audience' ? connection.audienceId : !connection.audienceId) &&
  connection.data?.locationId;

export const GoHighLevelSettings = ({
  connections,
  account,
}: {
  connections: ConnectionModel[];
  account: GoHighLevelOauthToken;
}) => {
  const facebookPageId = useSelector(activeFacebookPageIdSelector);
  const audiences = useSelector(selectAudienceSorted);
  const availableTags = useSelector((state: StoreState) =>
    tagsOfKinds(state, ['regular']),
  );
  const locations = useSelector(
    (state: StoreState) => state.goHighLevel.locations,
  );
  const { getAll } = useTagsCRUDActions();
  const { getGoHighLevelLocations } = useConnectGoHighLevel();
  const { companyName = 'Customers.ai', isWhitelabelCompany } =
    useWhitelabelConfig();
  useEffect(() => {
    getAll(facebookPageId, true);
    getGoHighLevelLocations(facebookPageId, account.id, false);
  }, []);
  const connectionTemplate = useCallback(
    (extra: Partial<ConnectionModel> = {}): ConnectionModel =>
      ({
        active: true,
        name: 'GoHighLevel Connection',
        connectionKind: 'gohighlevel',
        gohighlevelParams: { oauthTokenId: account.id },
        ...extra,
      } as ConnectionModel),
    [account],
  );
  const [radioValue, setRadioValue] = useState<'all' | 'audience'>(
    connections.find(conn => conn.active)?.audienceId ? 'audience' : 'all',
  );
  const [allConnections, setAllConnections] =
    useState<ConnectionModel[]>(connections);
  const canAdd = locations.length && audiences.length;
  // all contacts webhook is always there, so 2 connections means 1 all contacts + 1 audience based
  const multipleConnections = allConnections.length > 2;

  const { create, update, deleteConnection } = useConnectionCRUDActions();
  const addConnection = () => {
    setAllConnections(prevAllConnections => [
      ...prevAllConnections,
      connectionTemplate(),
    ]);
  };
  const remove = async (index: number) => {
    const connection = allConnections[index];
    if (connection.id) {
      await deleteConnection(facebookPageId, connection);
    }
    setAllConnections(prevAllConnections =>
      prevAllConnections.filter(conn => conn !== connection),
    );
  };
  const updateOrEdit = async (
    index: number,
    updateData: Partial<ConnectionModel>,
  ) => {
    const connection = allConnections[index];
    if (connection.id) {
      const updatedConnection = await update(facebookPageId, {
        id: connection.id,
        ...updateData,
      });
      if (updatedConnection) {
        setAllConnections(prevAllConnections =>
          Object.assign([], prevAllConnections, { [index]: updatedConnection }),
        );
      }
    } else if (validConnection(radioValue, { ...connection, ...updateData })) {
      const createdConnection = await create(facebookPageId, {
        ...connection,
        ...updateData,
      });
      if (createdConnection) {
        setAllConnections(prevAllConnections =>
          Object.assign([], prevAllConnections, { [index]: createdConnection }),
        );
      }
    } else {
      setAllConnections(prevAllConnections =>
        Object.assign([], prevAllConnections, {
          [index]: { ...connection, ...updateData },
        }),
      );
    }
  };

  const onChangeType = (type: 'all' | 'audience') => {
    const cond = conn =>
      type === 'all'
        ? conn.id && !conn.audienceId
        : conn.audienceId || !conn.id;
    return async () => {
      const promises = allConnections.map((conn, index) => {
        if (cond(conn)) {
          return updateOrEdit(index, { active: true });
        }
        return updateOrEdit(index, { active: false });
      });
      if (allConnections.length === 1) {
        // just one connection is only possible if the user
        // has only configured no audiences setup.
        addConnection();
      } else {
        await Promise.all(promises);
      }
      setRadioValue(type);
    };
  };

  return (
    <div className={styles.settingsContent}>
      <div
        className={classNames(styles.box, {
          [styles.boxDisabled]: account.readOnly,
        })}
      >
        <div className={styles.boxWithHelp}>
          <div className={classNames(styles.heading, styles.headingWithSwitch)}>
            Contact sync
          </div>
        </div>
        <div className={styles.radioInputs}>
          <RadioInput
            name="all"
            value="all"
            label={`Sync all ${companyName} contacts to HighLevel`}
            onChange={onChangeType('all')}
            checked={radioValue === 'all'}
          />
          <RadioInput
            name="audience"
            value="audience"
            label="Choose an audience to sync from"
            onChange={onChangeType('audience')}
            checked={radioValue === 'audience'}
          />
        </div>
        <div className={styles.gridContainer}>
          {radioValue === 'audience' ? (
            <>
              <div className={styles.gridAudiences}>
                <div className={classNames(styles.gridColumn, styles.firstRow)}>
                  {!isWhitelabelCompany && (
                    <CustomersAiLogo className={styles.gridIcon} />
                  )}{' '}
                  {companyName}
                  Audience
                </div>
                <div
                  className={classNames(styles.gridColumn, styles.firstRow)}
                />
                <div className={classNames(styles.gridColumn, styles.firstRow)}>
                  <GoHighLevelIcon className={styles.gridIcon} /> HighLevel
                  Location
                </div>
                <div className={classNames(styles.gridColumn, styles.firstRow)}>
                  Add tags to HighLevel contacts
                </div>
                <div className={classNames(styles.gridColumn, styles.firstRow)}>
                  Actions
                </div>
                {allConnections.map((conn, idx) => {
                  if (!conn.id || conn.audienceId) {
                    const key = `connection-${idx}`;
                    return (
                      <GoHighLevelConnectionEditor
                        key={key}
                        canDelete={multipleConnections}
                        connection={conn}
                        withAudiences={true}
                        locations={locations}
                        audiences={audiences}
                        update={props => updateOrEdit(idx, props)}
                        remove={() => remove(idx)}
                        activeFbPageId={facebookPageId}
                        availableTags={availableTags}
                      />
                    );
                  }
                  return null;
                })}
              </div>
              {canAdd ? (
                <Button
                  css={{ container: styles.addButton }}
                  color="secondary-blue"
                  onClick={addConnection}
                >
                  <PlusOutlined />
                  Add
                </Button>
              ) : null}
            </>
          ) : (
            <div className={styles.gridAll}>
              <div className={classNames(styles.gridColumn, styles.firstRow)}>
                <GoHighLevelIcon className={styles.gridIcon} /> HighLevel
                Location
              </div>
              <div className={classNames(styles.gridColumn, styles.firstRow)}>
                Add tags to HighLevel contacts
              </div>
              <div className={classNames(styles.gridColumn, styles.firstRow)}>
                Actions
              </div>
              {allConnections.map((conn, idx) => {
                const key = `connection-${idx}`;
                if (conn.id && !conn.audienceId) {
                  return (
                    <GoHighLevelConnectionEditor
                      key={key}
                      canDelete={false}
                      connection={conn}
                      withAudiences={false}
                      locations={locations}
                      audiences={audiences}
                      update={props => updateOrEdit(idx, props)}
                      activeFbPageId={facebookPageId}
                      availableTags={availableTags}
                    />
                  );
                }
                return null;
              })}
            </div>
          )}
          <div className={styles.note}>
            All contacts will have a tag “capture” if no tag is selected.
          </div>
        </div>
      </div>
    </div>
  );
};
